import React, { useRef, useState } from "react";
import { FaUpload, FaTimes, FaImage, FaLanguage, FaVideo } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useApi } from "../../context/ApiContext";
import { getThumbnailUrl } from "../../utils/imageUtils";
import { SupportedLanguage } from "../../types/translations.types";
import { Progress } from "./Progress";

// Helper function to check if URL is a video
const isVideoUrl = (url: string): boolean => {
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

// Helper function to check if file is a video
const isVideoFile = (file: File): boolean => {
  return file.type.startsWith("video/");
};

// Separate component for media items (images and videos) to allow useRef
const MediaItem: React.FC<{
 media: { url: string; alt?: string; isPrimary?: boolean; translations?: { [languageCode: string]: string }; type?: "image" | "video" };
 index: number;
 currentLanguage: SupportedLanguage;
 accept: string;
 uploading: boolean;
 onFileSelect: (files: FileList | null) => void;
 onImageSetPrimary?: (index: number) => void;
 onImageRemove?: (index: number) => void;
 onImageTranslationUpload?: (imageIndex: number, url: string) => void;
 getImageUrl: (image: { url: string; translations?: { [languageCode: string]: string } }) => string;
 hasTranslation: (image: { translations?: { [languageCode: string]: string } }) => boolean;
}> = ({
 media,
 index,
 currentLanguage,
 accept,
 uploading,
 onFileSelect,
 onImageSetPrimary,
 onImageRemove,
 onImageTranslationUpload,
 getImageUrl,
 hasTranslation,
}) => {
 const translationInputRef = useRef<HTMLInputElement>(null);
 const mediaUrl = getImageUrl(media);
 const translated = hasTranslation(media);
 const { t } = useTranslation();
 const isVideo = media.type === "video" || isVideoUrl(mediaUrl);
 
 return (
  <div className="relative group">
   {isVideo ? (
    <video
     src={mediaUrl}
     className="w-full h-24 object-cover border border-gray-200 rounded"
     preload="metadata"
     muted
    >
     {t("admin.productForm.videoNotSupported") || "Your browser does not support video playback"}
    </video>
   ) : (
    <img
     src={getThumbnailUrl(mediaUrl)}
     alt={media.alt || t("admin.productForm.image") + ` ${index + 1}`}
     className="w-full h-24 object-cover border border-gray-200 rounded"
    />
   )}
   {isVideo && (
    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
     <FaVideo className="text-white text-2xl" />
    </div>
   )}
   {media.isPrimary && currentLanguage === "en" && (
    <span className="absolute top-1 left-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded">
     {t("admin.productForm.primary")}
    </span>
   )}
   {currentLanguage !== "en" && (
    <span className={`absolute top-1 left-1 text-white text-xs px-1.5 py-0.5 rounded ${
     translated ? "bg-green-600" : "bg-yellow-600"
    }`}>
     {translated ? "✓" : "!"}
    </span>
   )}
   <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 rounded">
    {currentLanguage === "en" && (
     <>
      {!media.isPrimary && onImageSetPrimary && !isVideo && (
       <button
        type="button"
        onClick={() => onImageSetPrimary?.(index)}
        className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
        title={t("admin.productForm.setPrimary")}
       >
        {t("admin.productForm.primary")}
       </button>
      )}
      {onImageRemove && (
       <button
        type="button"
        onClick={() => onImageRemove(index)}
        className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
        title={t("admin.productForm.removeImage")}
       >
        <FaTimes />
       </button>
      )}
     </>
    )}
    {currentLanguage !== "en" && onImageTranslationUpload && (
     <>
      <input
       ref={translationInputRef}
       type="file"
       accept={accept}
       onChange={(e) => onFileSelect(e.target.files)}
       className="hidden"
      />
      <button
       type="button"
       onClick={() => translationInputRef.current?.click()}
       className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700 flex items-center gap-1"
       title={translated ? t("admin.productForm.replace") : t("admin.productForm.translate")}
       disabled={uploading}
      >
       <FaLanguage className="text-xs" />
       {translated ? t("admin.productForm.replace") : t("admin.productForm.translate")}
      </button>
     </>
    )}
   </div>
  </div>
 );
};

interface FileUploaderProps {
 onUploadComplete: (url: string, type?: "image" | "video") => void;
 onImageTranslationUpload?: (imageIndex: number, url: string) => void;
 accept?: string;
 multiple?: boolean;
 label?: string;
 currentLanguage?: SupportedLanguage;
 existingImages?: Array<{ 
  url: string; 
  alt?: string; 
  isPrimary?: boolean;
  translations?: { [languageCode: string]: string };
  type?: "image" | "video";
 }>;
 onImageRemove?: (index: number) => void;
 onImageSetPrimary?: (index: number) => void;
 additionalActions?: React.ReactNode;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
 onUploadComplete,
 onImageTranslationUpload,
 accept = "image/*,video/*",
 multiple = true,
 label = "Upload Images & Videos",
 currentLanguage = "en",
 existingImages = [],
 onImageRemove,
 onImageSetPrimary,
 additionalActions,
}) => {
 const fileInputRef = useRef<HTMLInputElement>(null);
 const { api } = useApi();
 const { t } = useTranslation();
 const [uploading, setUploading] = useState(false);
 const [dragActive, setDragActive] = useState(false);
 const [uploadingFiles, setUploadingFiles] = useState<Record<string, { name: string; type: "image" | "video"; progress: number }>>({});

 const handleFileSelect = async (files: FileList | null, imageIndex?: number) => {
  if (!files || files.length === 0) return;

  setUploading(true);
  
  // Initialize progress tracking for all files
  const fileArray = Array.from(files);
  const initialFiles: Record<string, { name: string; type: "image" | "video"; progress: number }> = {};
  
  fileArray.forEach((file) => {
    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    initialFiles[fileKey] = {
      name: file.name,
      type: isVideoFile(file) ? "video" : "image",
      progress: 0,
    };
  });
  
  setUploadingFiles(initialFiles);

  try {
   for (let i = 0; i < fileArray.length; i++) {
    const file = fileArray[i];
    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    const isVideo = isVideoFile(file);
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post("/admin/upload", formData, {
     headers: {
      "Content-Type": "multipart/form-data",
     },
     onUploadProgress: (progressEvent: { loaded: number; total?: number }) => {
      if (progressEvent.total) {
       const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
       setUploadingFiles((prev) => ({
        ...prev,
        [fileKey]: { ...prev[fileKey], progress: percentCompleted },
       }));
      }
     },
    });

    if (response.data.url) {
     if (imageIndex !== undefined && onImageTranslationUpload) {
      // Uploading translation for existing media
      onImageTranslationUpload(imageIndex, response.data.url);
     } else {
      // Uploading new media (English only)
      onUploadComplete(response.data.url, isVideo ? "video" : "image");
     }
    }
    
    // Remove completed file from uploading list
    setUploadingFiles((prev) => {
     const updated = { ...prev };
     delete updated[fileKey];
     return updated;
    });
   }
  } catch (error) {
   console.error("Upload error:", error);
   alert(t("admin.productForm.failedToUploadImage"));
  } finally {
   setUploading(false);
   setUploadingFiles({});
   if (fileInputRef.current) {
    fileInputRef.current.value = "";
   }
  }
 };

 const handleDrag = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.type === "dragenter" || e.type === "dragover") {
   setDragActive(true);
  } else if (e.type === "dragleave") {
   setDragActive(false);
  }
 };

 const handleDrop = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setDragActive(false);
  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
   handleFileSelect(e.dataTransfer.files);
  }
 };

 // Get the image URL to display based on current language
 const getImageUrl = (image: { url: string; translations?: { [languageCode: string]: string } }): string => {
  if (currentLanguage === "en") {
   return image.url;
  }
  // For other languages, use translation if available, otherwise fallback to original
  return image.translations?.[currentLanguage] || image.url;
 };

 // Check if image has translation for current language
 const hasTranslation = (image: { translations?: { [languageCode: string]: string } }): boolean => {
  if (currentLanguage === "en") return true;
  return !!image.translations?.[currentLanguage];
 };

 return (
  <div className="space-y-2">
   <label className="block text-sm font-medium text-gray-700">
    {label}
   </label>

   {/* Upload Area - Only show for English */}
   {currentLanguage === "en" && (
    <div
     className={`border-2 border-dashed rounded-lg transition-colors ${
      dragActive
       ? "border-blue-500 bg-blue-50"
       : "border-gray-300 hover:border-gray-400"
     } ${uploading ? "opacity-50 pointer-events-none" : ""} ${
      existingImages.length > 0 ? "p-3" : "p-6"
     }`}
     onDragEnter={handleDrag}
     onDragLeave={handleDrag}
     onDragOver={handleDrag}
     onDrop={handleDrop}
    >
     <input
      ref={fileInputRef}
      type="file"
      accept={accept}
      multiple={multiple}
      onChange={(e) => handleFileSelect(e.target.files)}
      className="hidden"
     />
     
     {/* Show upload prompt only if no images or in empty state */}
     {existingImages.length === 0 && Object.keys(uploadingFiles).length === 0 ? (
      <div className="text-center">
       <FaImage className="mx-auto text-4xl text-gray-400 mb-2" />
       <p className="text-sm text-gray-600 mb-2">
        {t("admin.productForm.dragAndDropImages")}
       </p>
       <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded"
        disabled={uploading}
       >
        <FaUpload />
        {uploading ? t("admin.productForm.uploading") : t("admin.productForm.selectFiles")}
       </button>
      </div>
     ) : (
      <div className="space-y-3">
       {/* Upload button when images exist */}
       <div className="flex justify-center gap-2 flex-wrap">
        <button
         type="button"
         onClick={() => fileInputRef.current?.click()}
         className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded text-sm"
         disabled={uploading}
        >
         <FaUpload />
         {uploading ? t("common.loading") : t("admin.productForm.addMoreImages")}
        </button>
        {additionalActions}
       </div>
       
       {/* Media grid inside upload zone */}
       <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {existingImages.map((media, index) => {
         const mediaType = media.type || (isVideoUrl(media.url) ? "video" : "image");
         return (
          <MediaItem
           key={index}
           media={{ ...media, type: mediaType }}
           index={index}
           currentLanguage={currentLanguage}
           accept={accept}
           uploading={uploading}
           onFileSelect={(files) => handleFileSelect(files, index)}
           onImageSetPrimary={onImageSetPrimary}
           onImageRemove={onImageRemove}
           onImageTranslationUpload={onImageTranslationUpload}
           getImageUrl={getImageUrl}
           hasTranslation={hasTranslation}
          />
         );
        })}
        
        {/* Show uploading files with progress */}
        {Object.entries(uploadingFiles).map(([fileKey, file]) => (
         <div key={`uploading-${fileKey}`} className="relative group border border-gray-200 rounded bg-gray-50">
          <div className="w-full h-24 flex items-center justify-center border border-gray-200 rounded">
           {file.type === "video" ? (
            <div className="flex flex-col items-center gap-2">
             <FaVideo className="text-gray-400 text-2xl" />
             <span className="text-xs text-gray-600 truncate max-w-full px-2">{file.name}</span>
            </div>
           ) : (
            <div className="flex flex-col items-center gap-2">
             <FaImage className="text-gray-400 text-2xl" />
             <span className="text-xs text-gray-600 truncate max-w-full px-2">{file.name}</span>
            </div>
           )}
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-black bg-opacity-75 rounded-b">
           <div className="flex justify-between text-xs text-white mb-1">
            <span className="truncate">{file.name}</span>
            <span>{file.progress}%</span>
           </div>
           <Progress value={file.progress} max={100} size="sm" className="bg-gray-600" />
          </div>
         </div>
        ))}
       </div>
      </div>
     )}
    </div>
   )}

   {/* Translation Notice for non-English languages */}
   {currentLanguage !== "en" && existingImages.length === 0 && (
    <div className="border border-gray-300 p-4 text-center text-gray-600 bg-gray-50 rounded">
     <FaLanguage className="mx-auto text-2xl mb-2 text-gray-400" />
     <p className="text-sm">
      {t("admin.productForm.switchToEnglishToUpload")}
     </p>
    </div>
   )}

   {/* Existing Media for non-English (translation mode) */}
   {currentLanguage !== "en" && existingImages.length > 0 && (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-3">
     <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {existingImages.map((media, index) => {
       const mediaType = media.type || (isVideoUrl(media.url) ? "video" : "image");
       return (
        <MediaItem
         key={index}
         media={{ ...media, type: mediaType }}
         index={index}
         currentLanguage={currentLanguage}
         accept={accept}
         uploading={uploading}
         onFileSelect={(files) => handleFileSelect(files, index)}
         onImageSetPrimary={onImageSetPrimary}
         onImageRemove={onImageRemove}
         onImageTranslationUpload={onImageTranslationUpload}
         getImageUrl={getImageUrl}
         hasTranslation={hasTranslation}
        />
       );
      })}
     </div>
    </div>
   )}
  </div>
 );
};

